import type { Project } from '@teemill/projects';
import { computedWithControl } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useRouter, type RouteLocationRaw } from 'vue-router';

export const useProjectStore = defineStore('project', () => {
  const router = useRouter();

  const ready = ref(false);
  const projects = ref<Project[]>([]);
  const redirect = ref<RouteLocationRaw>();

  const projectQuery = useRouteQuery<string>('project');

  const find = (project: string) => projects.value.find((p: Project) => p.id === project);

  const active = computedWithControl(
    () => find(projectQuery.value)?.id,
    () => find(projectQuery.value),
  );

  const update = (updatedProject: Project): void => {
    const index = projects.value.findIndex((p) => p.id === updatedProject.id);

    if (index !== -1) {
      projects.value = [
        ...projects.value.slice(0, index),
        updatedProject,
        ...projects.value.slice(index + 1),
      ];
    } else {
      projects.value = [...projects.value, updatedProject];
    }

    router.isReady().then(() => {
      ready.value = true;
    });
  };

  const updateAll = (updatedProjects: Project[]): void => {
    projects.value = updatedProjects;

    router.isReady().then(() => {
      ready.value = true;
    });
  };

  const updateRedirect = (updatedRedirect?: RouteLocationRaw): void => {
    redirect.value = updatedRedirect;
  };

  const remove = (deletedProject: Project): void => {
    const index = projects.value.findIndex((p) => p.id === deletedProject.id);

    if (index !== -1) {
      projects.value.splice(index, 1);
    }
  };

  return {
    ready,
    projects,
    redirect,
    active,
    find,
    update,
    updateAll,
    updateRedirect,
    remove,
  };
});
